import React from 'react'
import { pinsAreAvailableForAllCircuits } from 'utils/circuits'
import { ChecTooltip } from 'components/chec'
import { Set } from 'immutable'
import {
  isEmpty,
  pluck,
  concat,
  compose,
  and,
  has,
  defaultTo,
  path,
  not,
  propOr,
} from 'ramda'
import { isArray, isFalse } from 'utils/types'
import { MAX_MODULE_COUNTS, ERROR, WARNING } from 'utils/constants'

export function generateStagedProp(id) {
  return `${id}staged`
}

// PERFORMANCE: This function is the least performant piece of the software so far. It's loops on loops so that's not surprising, and it's not a huge
// performance hit. But it's the first thing to look at when needing to increase performance of the application.
export function getDataAndColumnsFromProps(
  vehiclesToCompare,
  revertVehicleChanges,
  ThComponent,
  CellComponent
) {
  let hasStagedReferenceParameters = false
  let isLoading = false
  let allReferenceParameters = []

  // O(v * (v.rp * rp + (v.rp - rp)))
  // ^ funky notation that means "this is the loop to look at first". Look into making allReferenceParameters a hash perhaps.
  vehiclesToCompare.forEach((vehicle) => {
    if (vehicle.isLoading) isLoading = true
    if (vehicle.stagedReferenceParameters.length)
      hasStagedReferenceParameters = true

    const vehicleRPs = vehicle.referenceParameters || []
    const referenceParametersToAdd = vehicleRPs.filter(
      (vehicleRP) =>
        !allReferenceParameters.some(
          (existingRP) => existingRP.uri === vehicleRP.uri
        )
    )
    allReferenceParameters = allReferenceParameters.concat(
      referenceParametersToAdd
    )
  })

  // O(v * (rp * (v.rp + v.srp)))
  const data = allReferenceParameters
    .map((rp) => {
      const rpId = rp.refParameterNumber
      const rpUri = rp.uri

      return vehiclesToCompare.reduce(
        (row, vehicle) => {
          const { serialNumber } = vehicle

          row[serialNumber] =
            vehicle.referenceParameters &&
            vehicle.referenceParameters.some((vRp) => vRp.uri === rpUri)
          row[generateStagedProp(serialNumber)] =
            vehicle.stagedReferenceParameters &&
            vehicle.stagedReferenceParameters.some((vRp) => vRp.uri === rpUri)
          row.isLoading = isLoading
          row.rp = rp

          return row
        },
        {
          key: rpUri,
          rpUri,
          paramHeader: `${rpId} ${rp.serviceDescription}`,
          releaseHeader: `${rp.release} ${rp.status}`,
        }
      )
    })
    .sort((a, b) =>
      a.paramHeader.localeCompare(b.paramHeader, {
        sensitiviy: 'variant',
      })
    )
  const headerStyle = (props) => ({
    style: {
      boxSizing: 'border-box',
      overflow: 'hidden',
      height: hasStagedReferenceParameters ? '82px' : '52px',
      ...(props.centered && { textAlign: 'center' }),
    },
  })
  const columns = [
    {
      onHeaderCell: headerStyle,
      title: ThComponent({
        title: 'Parameters',
        revertVisible: hasStagedReferenceParameters,
      }),
      dataIndex: 'paramHeader',
      key: 'paramHeader',
      width: 260,
      className: 'param-id-column',
      render: (text) => <ChecTooltip title={text}>{text}</ChecTooltip>,
    },
    {
      onHeaderCell: headerStyle,
      title: ThComponent({
        title: 'Version',
        revertVisible: hasStagedReferenceParameters,
      }),
      dataIndex: 'releaseHeader',
      key: 'releaseHeader',
      width: 80,
      render: (text) => <ChecTooltip title={text}>{text}</ChecTooltip>,
    },
  ].concat(
    vehiclesToCompare.map((v) => ({
      onHeaderCell: (p) => headerStyle({ ...p, centered: true }),
      title: ThComponent({
        title: v.serialNumber,
        vehicleHasStagedRPs: data.some(
          (d) => d[generateStagedProp(v.serialNumber)]
        ),
        revertVisible: hasStagedReferenceParameters,
        revertVehicleChanges,
      }),
      dataIndex: v.serialNumber,
      render: (param, renderRow = {}) =>
        CellComponent({
          vehicleId: v.serialNumber,
          rpUri: renderRow.uri,
          renderRow,
        }),
    }))
  )

  return {
    data,
    columns,
    hasStagedReferenceParameters,
    isLoading,
  }
}

// When assigning a new RP to a vehicle, it is necessary to determine whether or not
// any circuits named in the RP already are assigned to a pin on the truck.
export const rpCircuitsRequirePinAssignment = (
  rpCircuits,
  vehicleCtpDictionary,
  pendingAssignments,
  vehicleType
) => {
  // vehicleModules is an array of modules. Each module has an array of connectors,
  // each of which has an array of pins. Flatten such that we have a single array of all the pins
  const vehiclePins = Object.values(vehicleCtpDictionary).filter((entity) =>
    and(has('pinType', entity), has('circuitNumber', entity))
  )

  const circuitsMappedToVehicle = compose(
    Set,
    pluck('circuitNumber'),
    concat(pendingAssignments)
  )(vehiclePins)

  const circuitsThatRequireMapping = rpCircuits
    .map((circuit) => {
      const mapped =
        circuitsMappedToVehicle.has(circuit.circuitNumber) &&
        isEmpty(
          pendingAssignments.filter(
            (assignment) => assignment.circuitNumber === circuit.circuitNumber
          )
        )
      return {
        ...circuit,
        mapped,
      }
    })
    .filter((circuit) => !circuit.mapped)

  const pinsAreAvailableForMappingCircuits =
    !isEmpty(circuitsThatRequireMapping) &&
    pinsAreAvailableForAllCircuits(
      circuitsThatRequireMapping,
      vehicleCtpDictionary,
      vehicleType
    )
  return {
    circuitsThatRequireMapping,
    pinsAreAvailableForMappingCircuits:
      pinsAreAvailableForMappingCircuits || false,
  }
}

export function getOutputBackgroundColor(avail, tot) {
  const available = Number(avail)
  const total = Number(tot)
  if (available === 0 || available < 1) {
    return ERROR
  }
  if ((total - available) / total > 0.75) {
    return WARNING
  }
  return null
}

export const moduleFeatureResourceCounts = (features) =>
  features.reduce((acc, item) => {
    if (!isArray(features)) {
      throw new TypeError(
        'Invalid arguments passed to `moduleFeatureResourceCounts`'
      )
    }

    // don't count these as used resources
    if (item.isMarkedForDeletion) return acc

    const prevCounts = defaultTo(
      {
        complexQueryCount: 0,
        timerQueryCount: 0,
      },
      acc[item.moduleName]
    )

    return {
      ...acc,
      [item.moduleName]: {
        complexQueryCount:
          prevCounts.complexQueryCount + item.complexQueryCount,
        timerQueryCount: prevCounts.timerQueryCount + item.timerQueryCount,
      },
    }
  }, {})

export const findLogicCountConflicts = (
  record,
  moduleCounts,
  moduleCapacity = {}
) => {
  const {
    moduleName,
    moduleType,
    latestOnVehicle,
    latestSnapshot,
    isMarkedForDeletion,
  } = record

  // return false if the record has been added succesfully
  if (latestOnVehicle && not(isMarkedForDeletion)) {
    return {
      isOverQueryCount: false,
      isOverTimerCount: false,
    }
  }

  const recordToCompare =
    isFalse(latestOnVehicle) && not(isMarkedForDeletion)
      ? path(['object', 'feature'], latestSnapshot) || record
      : record

  const { complexQueryCount = 0, timerQueryCount = 0 } = recordToCompare || {}

  if (!MAX_MODULE_COUNTS[moduleType]) {
    return { isOverQueryCount: false, isOverTimerCount: false }
  }
  const {
    MAX_COMPLEX_QUERY_COUNT: defaultMaxComplexQueryCount,
    MAX_TIMER_QUERY_COUNT: defaultMaxTimerQueryCount,
  } = MAX_MODULE_COUNTS[moduleType]
  const maxComplexQueryCount =
    typeof moduleCapacity.maxLogic === 'number' &&
    Number.isFinite(moduleCapacity.maxLogic) &&
    moduleCapacity.maxLogic > 0
      ? moduleCapacity.maxLogic
      : defaultMaxComplexQueryCount
  const maxTimerQueryCount =
    typeof moduleCapacity.maxTimer === 'number' &&
    Number.isFinite(moduleCapacity.maxTimer) &&
    moduleCapacity.maxTimer > 0
      ? moduleCapacity.maxTimer
      : defaultMaxTimerQueryCount

  const queryCount = (prop) => propOr(0, prop, path([moduleName], moduleCounts))

  // Marked-for-deletion rows are excluded from moduleCounts, so re-adding one
  // must project its full resource count back into the module total.
  const projectedComplexQueryCount = isMarkedForDeletion
    ? complexQueryCount
    : complexQueryCount - propOr(0, 'complexQueryCount', record)
  const isOverQueryCount =
    queryCount('complexQueryCount') + projectedComplexQueryCount >
    maxComplexQueryCount

  const projectedTimerQueryCount = isMarkedForDeletion
    ? timerQueryCount
    : timerQueryCount - propOr(0, 'timerQueryCount', record)
  const isOverTimerCount =
    queryCount('timerQueryCount') + projectedTimerQueryCount >
    maxTimerQueryCount

  return { isOverQueryCount, isOverTimerCount }
}
